import {
  CheckCircleRounded,
  ConfirmationNumberOutlined,
  QrCode2Outlined,
  Storefront,
  SyncProblemOutlined,
  VerifiedRounded,
} from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import QRCode from "react-qr-code";
import useReceptorPantallaCliente from "../hooks/useReceptorPantallaCliente";
import { formatCurrency } from "../utils";
import { ESTADOS_PAGO, ESTADOS_PANTALLA, totalLinea } from "./canal";

// Pantalla que mira al comprador, en una pestaña aparte. Sólo dibuja lo que
// llega por el canal: nunca consulta el backend ni conoce otra operación.
// Todos los tamaños son relativos al viewport para leerse a distancia de
// mostrador en cualquier monitor. El tema fuerza `fontSize: inherit` en los
// SvgIcon, así que cada ícono toma el tamaño de la caja que lo envuelve.

const FONDO = "linear-gradient(118deg, #20245F 0%, #2C3087 62%, #3E46A4 100%)";
const PANEL = {
  bgcolor: "rgba(255,255,255,.08)",
  border: "1px solid rgba(255,255,255,.14)",
  borderRadius: "clamp(18px, 2.4vw, 32px)",
  p: "clamp(16px, 2.4vw, 36px)",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
};
const ETIQUETA = {
  fontSize: "clamp(11px, 1.15vw, 18px)",
  fontWeight: 900,
  letterSpacing: ".14em",
  color: "rgba(255,255,255,.62)",
};

function Encabezado({ evento }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: "clamp(16px, 3vw, 48px)",
        py: "clamp(12px, 2vh, 24px)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "clamp(10px, 1.2vw, 18px)", minWidth: 0 }}>
        <Box
          sx={{
            width: "clamp(40px, 4.2vw, 64px)",
            height: "clamp(40px, 4.2vw, 64px)",
            borderRadius: "28%",
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(255,255,255,.12)",
            flexShrink: 0,
            fontSize: "clamp(22px, 2.4vw, 36px)",
          }}
        >
          <Storefront />
        </Box>
        <Typography noWrap sx={{ fontSize: "clamp(18px, 2.2vw, 34px)", fontWeight: 900, lineHeight: 1.1 }}>
          {evento || "UPrO Comedor"}
        </Typography>
      </Box>
      <Typography sx={{ ...ETIQUETA, flexShrink: 0 }}>UPRO COMEDOR</Typography>
    </Box>
  );
}

Encabezado.propTypes = { evento: PropTypes.string };

function Centrado({ icono, titulo, detalle, children }) {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: "clamp(10px, 1.6vh, 24px)",
        px: "clamp(16px, 4vw, 64px)",
      }}
    >
      {icono}
      <Typography sx={{ fontSize: "clamp(34px, 5.4vw, 96px)", fontWeight: 900, lineHeight: 1.05 }}>
        {titulo}
      </Typography>
      {detalle && (
        <Typography sx={{ fontSize: "clamp(18px, 2.2vw, 40px)", color: "rgba(255,255,255,.78)", maxWidth: "26em" }}>
          {detalle}
        </Typography>
      )}
      {children}
    </Box>
  );
}

Centrado.propTypes = {
  icono: PropTypes.node,
  titulo: PropTypes.node.isRequired,
  detalle: PropTypes.node,
  children: PropTypes.node,
};

function IconoGrande({ children, color = "rgba(255,255,255,.12)" }) {
  return (
    <Box
      sx={{
        width: "clamp(96px, 14vh, 200px)",
        height: "clamp(96px, 14vh, 200px)",
        borderRadius: "30%",
        display: "grid",
        placeItems: "center",
        bgcolor: color,
        fontSize: "clamp(56px, 8vh, 120px)",
      }}
    >
      {children}
    </Box>
  );
}

IconoGrande.propTypes = { children: PropTypes.node.isRequired, color: PropTypes.string };

function Standby({ evento }) {
  return (
    <Centrado
      icono={<IconoGrande><Storefront /></IconoGrande>}
      titulo="¡Hola!"
      detalle={
        evento
          ? `Bienvenido/a a ${evento}. Acercate al mostrador para hacer tu pedido.`
          : "Acercate al mostrador para hacer tu pedido."
      }
    />
  );
}

Standby.propTypes = { evento: PropTypes.string };

function Listo({ mensaje }) {
  const cubierto = mensaje.pg.e === ESTADOS_PAGO.NO_REQUERIDO;
  return (
    <Centrado
      icono={<IconoGrande color="rgba(103,193,184,.22)"><CheckCircleRounded sx={{ color: "#67C1B8" }} /></IconoGrande>}
      titulo="¡Gracias por tu compra!"
      detalle={cubierto ? "Tu pedido quedó cubierto con el voucher." : `Pago registrado · ${formatCurrency(mensaje.to)}`}
    >
      {mensaje.cl?.up && (
        <Typography sx={{ fontSize: "clamp(16px, 1.8vw, 30px)", color: "rgba(255,255,255,.62)" }}>
          Hasta la próxima, {mensaje.cl.n}.
        </Typography>
      )}
    </Centrado>
  );
}

Listo.propTypes = { mensaje: PropTypes.object.isRequired };

function Lineas({ items }) {
  const listaRef = useRef(null);
  // El último producto agregado tiene que verse aunque la lista desborde.
  useEffect(() => {
    const lista = listaRef.current;
    if (lista) lista.scrollTop = lista.scrollHeight;
  }, [items.length]);

  if (items.length === 0) {
    return (
      <Typography sx={{ fontSize: "clamp(18px, 2vw, 32px)", color: "rgba(255,255,255,.55)", py: 2 }}>
        Todavía no hay productos.
      </Typography>
    );
  }

  return (
    <Box ref={listaRef} sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 1 }}>
      {items.map((linea, indice) => (
        <Box
          // Una misma línea puede repetirse si el catálogo tiene nombres iguales:
          // el índice es la identidad que importa en una lista redibujada entera.
          key={`${indice}-${linea[0]}`}
          sx={{
            display: "grid",
            gridTemplateColumns: "auto minmax(0, 1fr) auto",
            alignItems: "center",
            gap: "clamp(10px, 1.4vw, 22px)",
            py: "clamp(8px, 1.2vh, 16px)",
            borderBottom: "1px solid rgba(255,255,255,.12)",
            fontSize: "clamp(18px, 2.1vw, 36px)",
          }}
        >
          <Typography
            sx={{
              minWidth: "2.2em",
              textAlign: "center",
              fontSize: "inherit",
              fontWeight: 900,
              borderRadius: ".4em",
              bgcolor: "rgba(255,255,255,.14)",
              px: ".3em",
            }}
          >
            {linea[1]}×
          </Typography>
          <Typography noWrap sx={{ fontSize: "inherit", fontWeight: 600 }}>
            {linea[0]}
          </Typography>
          <Typography sx={{ fontSize: "inherit", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
            {formatCurrency(totalLinea(linea))}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

Lineas.propTypes = { items: PropTypes.arrayOf(PropTypes.array).isRequired };

function FilaTotal({ etiqueta, valor, color, negativo = false }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2, color }}>
      <Typography sx={{ fontSize: "clamp(16px, 1.7vw, 28px)", fontWeight: 700, color: "inherit" }}>
        {etiqueta}
      </Typography>
      <Typography
        sx={{ fontSize: "clamp(16px, 1.7vw, 28px)", fontWeight: 800, color: "inherit", fontVariantNumeric: "tabular-nums" }}
      >
        {negativo ? "− " : ""}
        {formatCurrency(valor)}
      </Typography>
    </Box>
  );
}

FilaTotal.propTypes = {
  etiqueta: PropTypes.string.isRequired,
  valor: PropTypes.number.isRequired,
  color: PropTypes.string,
  negativo: PropTypes.bool,
};

function Comprador({ comprador }) {
  if (!comprador) {
    return <Typography sx={{ fontSize: "clamp(20px, 2.4vw, 40px)", fontWeight: 900 }}>Pedido en curso</Typography>;
  }
  return (
    <Box>
      <Typography sx={{ fontSize: "clamp(20px, 2.4vw, 40px)", fontWeight: 900, lineHeight: 1.15 }}>
        {comprador.up ? `Hola, ${comprador.n}` : comprador.n}
      </Typography>
      {comprador.up && (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: ".4em",
            mt: ".5em",
            px: ".8em",
            py: ".3em",
            borderRadius: "2em",
            bgcolor: "rgba(103,193,184,.22)",
            color: "#9FE3DA",
            fontSize: "clamp(13px, 1.3vw, 22px)",
            fontWeight: 900,
          }}
        >
          <VerifiedRounded /> Comunidad UPrO
        </Box>
      )}
    </Box>
  );
}

Comprador.propTypes = { comprador: PropTypes.shape({ n: PropTypes.string, up: PropTypes.bool }) };

function TotalGrande({ mensaje }) {
  const conDescuento = mensaje.de > 0;
  return (
    <Box>
      <Typography sx={ETIQUETA}>{conDescuento ? "SALDO A COBRAR" : "TOTAL"}</Typography>
      <Typography
        data-testid="pantalla-cliente-total"
        sx={{
          fontSize: "clamp(44px, 6.6vw, 120px)",
          fontWeight: 900,
          lineHeight: 1,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-.02em",
        }}
      >
        {formatCurrency(mensaje.to)}
      </Typography>
    </Box>
  );
}

TotalGrande.propTypes = { mensaje: PropTypes.object.isRequired };

// Panel derecho durante el cobro: reserva el espacio del QR al mayor tamaño
// que permite el alto. Hasta que se retome la etapa 5 `pg.qr` llega nulo y el
// mismo recuadro explica que el vendedor confirma el pago.
function Cobro({ mensaje }) {
  const { pg } = mensaje;
  const cubierto = pg.e === ESTADOS_PAGO.NO_REQUERIDO;
  return (
    <>
      <Typography sx={ETIQUETA}>{cubierto ? "SIN PAGO ADICIONAL" : `PAGO · ${(pg.m || "").toUpperCase()}`}</Typography>
      {/* El área es un contenedor de tamaño: el cuadrado toma el menor de su
          ancho y su alto, así que ocupa todo lo que el panel deja libre sin
          pisar el total. El fallback en vh es para navegadores sin cqh. */}
      <Box sx={{ flex: 1, minHeight: 0, containerType: "size", display: "grid", placeItems: "center" }}>
        <Box
          data-testid="pantalla-cliente-qr"
          sx={{
            width: "min(100%, 50vh)",
            "@supports (width: 1cqh)": { width: "min(100cqw, 100cqh)" },
            aspectRatio: "1 / 1",
            borderRadius: "clamp(16px, 2vw, 28px)",
            bgcolor: pg.qr ? "#fff" : "rgba(255,255,255,.08)",
            border: pg.qr ? "none" : "2px dashed rgba(255,255,255,.28)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            gap: "1.5vh",
            p: pg.qr ? "clamp(12px, 2vh, 28px)" : "clamp(16px, 3vh, 40px)",
          }}
        >
          {pg.qr ? (
            <QRCode value={pg.qr} size={256} fgColor="#20245F" style={{ height: "100%", width: "100%" }} viewBox="0 0 256 256" />
          ) : (
            <>
              <Box sx={{ lineHeight: 0, fontSize: "clamp(56px, 12vh, 160px)", color: cubierto ? "#FFD27A" : "rgba(255,255,255,.35)" }}>
                {cubierto ? <ConfirmationNumberOutlined /> : <QrCode2Outlined />}
              </Box>
              <Typography sx={{ fontSize: "clamp(16px, 1.9vw, 32px)", fontWeight: 700, color: "rgba(255,255,255,.85)" }}>
                {cubierto
                  ? "Tu voucher cubre el pedido completo."
                  : `Pagá por ${pg.m || "el medio indicado"} y esperá la confirmación del vendedor.`}
              </Typography>
            </>
          )}
        </Box>
      </Box>
      <TotalGrande mensaje={mensaje} />
    </>
  );
}

Cobro.propTypes = { mensaje: PropTypes.object.isRequired };

function Pedido({ mensaje }) {
  const enCobro = mensaje.st === ESTADOS_PANTALLA.COBRO;
  return (
    <Box
      sx={{
        height: "100%",
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.15fr) minmax(0, .85fr)",
        gap: "clamp(12px, 1.8vw, 28px)",
      }}
    >
      <Box sx={PANEL}>
        <Typography sx={{ ...ETIQUETA, mb: "1vh" }}>TU PEDIDO</Typography>
        <Lineas items={mensaje.it} />
        {mensaje.de > 0 && (
          <Box sx={{ pt: "1.5vh", mt: "auto", borderTop: "1px solid rgba(255,255,255,.18)", display: "grid", gap: ".6vh" }}>
            <FilaTotal etiqueta="Subtotal" valor={mensaje.sub} color="rgba(255,255,255,.72)" />
            <FilaTotal etiqueta="Descuento" valor={mensaje.de} color="#FFD27A" negativo />
          </Box>
        )}
      </Box>

      <Box sx={{ ...PANEL, justifyContent: "space-between", gap: "2vh" }}>
        {enCobro ? (
          <Cobro mensaje={mensaje} />
        ) : (
          <>
            <Comprador comprador={mensaje.cl} />
            <TotalGrande mensaje={mensaje} />
            <Typography sx={{ fontSize: "clamp(15px, 1.5vw, 26px)", color: "rgba(255,255,255,.7)" }}>
              {mensaje.pg.e === ESTADOS_PAGO.NO_REQUERIDO
                ? "Cubierto con voucher · sin pago adicional"
                : mensaje.pg.m
                  ? `Medio de pago: ${mensaje.pg.m}`
                  : "El vendedor está cargando tu pedido."}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}

Pedido.propTypes = { mensaje: PropTypes.object.isRequired };

function Contenido({ mensaje, versionIncompatible, soportado }) {
  if (!soportado) {
    return (
      <Centrado
        icono={<IconoGrande><SyncProblemOutlined /></IconoGrande>}
        titulo="Navegador no compatible"
        detalle="Este navegador no puede sincronizarse con el punto de venta. Abrí la pantalla en una versión actual de Chrome, Edge, Firefox o Safari."
      />
    );
  }
  if (versionIncompatible) {
    return (
      <Centrado
        icono={<IconoGrande color="rgba(245,155,36,.25)"><SyncProblemOutlined sx={{ color: "#F59B24" }} /></IconoGrande>}
        titulo="Hay que actualizar la pantalla"
        detalle="El punto de venta y esta pantalla tienen versiones distintas. Recargá las dos pestañas para que vuelvan a entenderse."
      />
    );
  }
  if (!mensaje || mensaje.st === ESTADOS_PANTALLA.STANDBY) return <Standby evento={mensaje?.ev} />;
  if (mensaje.st === ESTADOS_PANTALLA.LISTO) return <Listo mensaje={mensaje} />;
  return <Pedido mensaje={mensaje} />;
}

Contenido.propTypes = {
  mensaje: PropTypes.object,
  versionIncompatible: PropTypes.bool.isRequired,
  soportado: PropTypes.bool.isRequired,
};

export default function PantallaCliente() {
  const { mensaje, versionIncompatible, soportado } = useReceptorPantallaCliente();

  useEffect(() => {
    document.title = "Pantalla al cliente · UPrO Comedor";
  }, []);

  return (
    <Box
      data-testid="pantalla-cliente"
      data-estado={mensaje?.st || ESTADOS_PANTALLA.STANDBY}
      sx={{
        height: "100vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        background: FONDO,
        color: "#fff",
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          width: "38vh",
          height: "38vh",
          borderRadius: "50%",
          border: "7vh solid rgba(103, 193, 184, .14)",
          right: "-12vh",
          top: "-18vh",
          pointerEvents: "none",
        },
      }}
    >
      <Encabezado evento={mensaje?.ev} />
      <Box sx={{ flex: 1, minHeight: 0, px: "clamp(16px, 3vw, 48px)", pb: "clamp(16px, 3vh, 40px)", position: "relative", zIndex: 1 }}>
        <Contenido mensaje={mensaje} versionIncompatible={versionIncompatible} soportado={soportado} />
      </Box>
    </Box>
  );
}
